import {Component, OnInit} from '@angular/core';
import {Title} from '@angular/platform-browser';
import {FormControl} from '@angular/forms';
import {MatSlideToggleChange} from '@angular/material/slide-toggle';
import {ChartState} from '@rsp/models/chart.state';
import {DateSelectionFormGroup} from '@rsp/shared/component/date-selection/date-selection.form';
import {TenantsCommunityDatasDTO} from '@rsp/models/dto/tenants-community-data.dto';
import {TenantConfigService} from '@rsp/shared/services/tenant-config.service';
import {ImageUrlBuilderService} from '@rsp/shared/services/image-url-builder.service';
import {RspEndpointsService} from '@rsp/shared/services/rest/rsp-endpoints.service';
import {environment} from '@env/environment';
import {GeolokationDTO, ProductionSiteDTO} from '@rsp/models/dto/tenants-details.dto';
import {SelectControlOption} from '@rsp/shared/form/select.control';
import {IntervalEnum} from '@rsp/models/enum/interval.enum';
import {TenantsCommunityTotalDTO} from '@rsp/models/dto/tenants-community-totals.dto';
import {DateRangeDTO} from '@rsp/models/dto/date-range.dto';
import {
  getEndOfDay,
  getEndOfMonth,
  getEndOfWeek,
  getEndOfYear,
  getStartOfDay,
  getStartOfMonth,
  getStartOfWeek,
  getStartOfYear,
  getStyleColor,
  getUTCNow,
  sumConsumption,
  throttleRequest,
  validateChain
} from '@rsp/shared/util/util';
import {BehaviorSubject, forkJoin, Observable} from 'rxjs';
import {DTOHasDataValidator} from '@rsp/shared/component/loading-wrapper/dto-has-data-validator';
import {HttpResult} from '@rsp/shared/services/rest/api-http.service';
import {UserTrackingService} from '@rsp/shared/services/user-tracking.service';

export enum TenantsCommunityUsagesEnum { YEAR = 'year', MONTH = 'month', WEEK = 'week', DAY = 'day'}

// this is nikolas comment
@Component({
  selector: 'rsp-community',
  templateUrl: './community.component.html',
  styleUrls: ['./community.component.scss']
})

export class CommunityComponent implements OnInit {

  public chartStateCommunityProductionSupply: ChartState = new ChartState();

  public usagesEnum: typeof TenantsCommunityUsagesEnum = TenantsCommunityUsagesEnum;

  thccolor0: string;
  thccolor1: string;
  thccolor2: string;
  thccolor3: string;
  thccolor4: string;


  communityh: string;
  communityt: string;

  autarkie: string;


  private communityUsagesOptions: SelectControlOption<TenantsCommunityUsagesEnum>[] = [
    {option: 'letzter Tag', value: TenantsCommunityUsagesEnum.DAY},
    {option: 'aktuelle Woche', value: TenantsCommunityUsagesEnum.WEEK},
    {option: 'aktueller Monat', value: TenantsCommunityUsagesEnum.MONTH},
    {option: 'aktuelles Jahr', value: TenantsCommunityUsagesEnum.YEAR},
  ];

  public dateSelectionFormGroup: DateSelectionFormGroup = new DateSelectionFormGroup();

  public regions: number[] = [];
  public markers: Array<number[]> = [];
  public mappoints: Array<number[]> = [];

  public _tenantCommunityDatasDTO: TenantsCommunityDatasDTO;
  public _tenantCommunityUsagesDTO: TenantsCommunityTotalDTO[];
  public _tenantCommunityUsageDTO: TenantsCommunityTotalDTO;
  public _tenantCommunityTotalsDTO: TenantsCommunityTotalDTO[];

  public compareWithYesterday: boolean;
  public chartStateCommunitySelfSupply: ChartState = new ChartState();
  public chartStateCommunityActivity: ChartState = new ChartState();

  public powerplants: ProductionSiteDTO[] = [];
  public showPlants: boolean;
  public autarkieGradFormControl: FormControl;
  public autarkieGradOptions: SelectControlOption<TenantsCommunityUsagesEnum>[] = [];
  public isMock: boolean;


  private communityProductionConsumptionChain = new BehaviorSubject(undefined);

  constructor(
    private titleService: Title,
    public tenant: TenantConfigService,
    public imageUrlBuilder: ImageUrlBuilderService,
    private userTracker: UserTrackingService,
    private rspEndpointsService: RspEndpointsService) {

    this.isMock = environment.mock;

    this.autarkieGradFormControl = new FormControl();
    this.autarkieGradFormControl.setValue(this.usagesEnum.DAY);
    this.autarkieGradFormControl.valueChanges.subscribe((value) => {
      this.onChangeAutarkieGradChart(value);
    });


    this.communityUsagesOptions.forEach((option) => {
      this.autarkieGradOptions.push(option);
    });

    this.compareWithYesterday = false;
    this.showPlants = true;

  }

  private static isDisabledOptionAutarkieGrad(dto: TenantsCommunityTotalDTO): boolean {
    if (dto === undefined) {
      return true;
    }

    return (sumConsumption(dto) + dto.residualConsumption) === 0;
  }

  private static calcCommunityAutarkieGrad(dto: TenantsCommunityTotalDTO): string {
    if (dto !== undefined) {
      const consumption: number = sumConsumption(dto) + dto.residualConsumption;

      // Wurde überhaupt Strom verbraucht?
      const autarkie = consumption > 0 ? 100 * Math.min(1, sumConsumption(dto) / consumption) : null;

      if (autarkie) {
        return autarkie.toFixed(0) + ' %';
      }
    }
    return 'Kein Strom verbraucht';
  }


  public onChangeCommunityActivityChart(event: MatSlideToggleChange) {
    this.compareWithYesterday = event.checked;
    this.userTracker.trackEvent('Aktivität', 'Vergleich mit Vortag', this.compareWithYesterday ? 'Ein' : 'Aus');
  }

  public onChangeAutarkieGradChart(timespan: TenantsCommunityUsagesEnum) {
    this.initCommunityBalanceSums();
    this.userTracker.trackEvent('Autarkie', 'Intervall', `auf ${timespan}`);
  }

  public ngOnInit(): void {
    this.thccolor0 = getStyleColor('--graph-color0');
    this.thccolor1 = getStyleColor('--graph-color1');
    this.thccolor2 = getStyleColor('--graph-color2');
    this.thccolor3 = getStyleColor('--graph-color3');
    this.thccolor4 = getStyleColor('--graph-color4');

    if (this.tenant && this.tenant.getInfo()) {
      this.communityh = this.tenant.getInfo().communityHeaderText;
      this.communityt = this.tenant.getInfo().communityDetailText;
      this.titleService.setTitle(this.tenant.getId() + ' | Community');

      this.tenant.getProductionSites().then((dto) => {
        this.powerplants = dto;
      });

      this.userTracker.trackPageView();

      this.initMap();
      this.initData();
    }

    this.communityProductionConsumptionChain
      .pipe(throttleRequest((a) => this.rspEndpointsService.getTenantsCommunityDatas(a.start, a.end, a.interval),
        this.chartStateCommunityProductionSupply))
      .pipe(validateChain((a) => DTOHasDataValidator.validateCommunityDataDTO(a), this.chartStateCommunityProductionSupply))
      .subscribe((dto) => {
        this._tenantCommunityDatasDTO = dto.result;

        this._tenantCommunityDatasDTO.values = this._tenantCommunityDatasDTO.values.map((val) => {
          val.consumptionSums = val.consumptionSums.map((v) => 4 * v);
          val.productionSums = val.productionSums.map((v) => 4 * v);
          return val;
        });
      });
  }

  public onChangeChartFilter(startDate: Date, endDate: Date, interval: IntervalEnum) {
    this.communityProductionConsumptionChain.next({start: startDate, end: endDate, interval: interval});
  }

  public onChartEvent(event) {
    console.log('chartEvent', event);
  }

  private initData() {
    let yesterday: Date = new Date(getUTCNow());

    yesterday.setDate(yesterday.getDate() - 1);
    yesterday = getEndOfDay(yesterday);

    let dayBeforeYesterday = new Date(yesterday);
    dayBeforeYesterday.setDate(dayBeforeYesterday.getDate() - 1);
    dayBeforeYesterday = getStartOfDay(dayBeforeYesterday);


    const interval: IntervalEnum = IntervalEnum.DAY;

    this.rspEndpointsService.getCommunityUsage(dayBeforeYesterday, yesterday, interval)
      .pipe(validateChain((dto) => DTOHasDataValidator.validateCommunityUsagesDTO(dto), this.chartStateCommunityActivity))
      .subscribe((dto) => {
        this._tenantCommunityTotalsDTO = dto.result;
      });

    this.loadCommunityActivity();

    this.rspEndpointsService.getCommunityDateRange().subscribe((dto) => {
      this.initCommunityDateRanges(dto.result.ranges);
    }, (err) => {
      console.error('Error retrieving Community date ranges');
    });

  }

  private loadCommunityActivity() {
    const now: Date = getUTCNow();


    let endDay = new Date(now);
    endDay.setDate(now.getDate() - 1);
    endDay = getEndOfDay(endDay);
    const startDay = getStartOfDay(endDay);

    const startWeek = getStartOfDay(getStartOfWeek(now));
    const endWeek = getEndOfDay(getEndOfWeek(now));

    const startMonth = getStartOfDay(getStartOfMonth(now));
    const endMonth = getEndOfDay(getEndOfMonth(now));

    const startYear = getStartOfDay(getStartOfYear(now));
    const endYear = getEndOfDay(getEndOfYear(now));


    const totalsDay: Observable<HttpResult<TenantsCommunityTotalDTO[]>> =
      this.rspEndpointsService.getCommunityUsage(startDay, endDay, IntervalEnum.DAY);
    const totalsWeek: Observable<HttpResult<TenantsCommunityTotalDTO[]>> =
      this.rspEndpointsService.getCommunityUsage(startWeek, endWeek, IntervalEnum.WEEK);
    const totalsMonth: Observable<HttpResult<TenantsCommunityTotalDTO[]>> =
      this.rspEndpointsService.getCommunityUsage(startMonth, endMonth, IntervalEnum.MONTH);
    const totalsYear: Observable<HttpResult<TenantsCommunityTotalDTO[]>> =
      this.rspEndpointsService.getCommunityUsage(startYear, endYear, IntervalEnum.YEAR);

    const totalsPromise = forkJoin([totalsDay, totalsWeek, totalsMonth, totalsYear]);
    totalsPromise.subscribe((dtos) => {
      this._tenantCommunityUsagesDTO = dtos.reduce((acc, dto) => {
        acc.push(dto.result.reduce((acc2, el: TenantsCommunityTotalDTO) => {


          if (acc2 === undefined) {
            acc2 = Object.assign({}, el);
          } else {
            acc2 = this.add(acc2, el);
          }


          return acc2;
        }, undefined));

        return acc;
      }, []);

      this.initCommunityBalanceSums();
    });


  }

  private initMap() {
    this.regions.push(+this.tenant.getInfo().longitude, +this.tenant.getInfo().latitude);

    Promise.all([this.tenant.getProductionSites(), this.tenant.getGeolocations()]).then((dtos) => {
      this.markers = (<ProductionSiteDTO[]>dtos[0]).map((powerplant) => {
        return [+powerplant.longitude, +powerplant.latitude, powerplant.energyType];
      });

      this.mappoints = (<GeolokationDTO[]>dtos[1]).map((geo) => {
        return [+geo.longitude, +geo.latitude, geo.type];
      });
    });
  }

  private initCommunityDateRanges(dateRanges: DateRangeDTO) {
    this.dateSelectionFormGroup.valueChanges.subscribe((val) => {
      this.onChangeChartFilter(val.date.start, val.date.end, val.interval);
    });
    this.dateSelectionFormGroup.init(dateRanges);
  }

  private initCommunityBalanceSums() {
    const timespan = this.autarkieGradFormControl.value;

    if (!this._tenantCommunityUsagesDTO && !timespan) {
      return;
    }

    const noDataAllDTO = this._tenantCommunityUsagesDTO.reduce((acc, cur) => {
      if (acc && DTOHasDataValidator.validateCommunityUsageDTO(cur)) {
        acc = false;
      }

      return acc;
    }, true);

    if (noDataAllDTO) {
      this.chartStateCommunitySelfSupply.hasNoData = true;
    }

    const index = this.autarkieGradOptions.findIndex((option) => option.value === timespan);

    const dto: TenantsCommunityTotalDTO = this._tenantCommunityUsagesDTO[index];
    this._tenantCommunityUsageDTO = dto;

    this.autarkie = CommunityComponent.calcCommunityAutarkieGrad(dto);

    this.autarkieGradOptions.forEach((option, i) => {
      const usageDTO: TenantsCommunityTotalDTO = this._tenantCommunityUsagesDTO[i];
      option.disabled = CommunityComponent.isDisabledOptionAutarkieGrad(usageDTO);
    });
  }

  public displayCommunityAutarkie(chartState: ChartState): boolean {
    return !chartState.hasNoData && !chartState.hasError;
  }

  private add(base: TenantsCommunityTotalDTO, a: TenantsCommunityTotalDTO): TenantsCommunityTotalDTO {
    base.bioProduction += a.bioProduction;
    base.waterProduction += base.waterProduction;
    base.pvProduction += a.pvProduction;
    base.windProduction += a.windProduction;

    base.bioConsumption += a.bioConsumption;
    base.waterConsumption += base.waterConsumption;
    base.pvConsumption += a.pvConsumption;
    base.windConsumption += a.windConsumption;
    base.residualConsumption += a.residualConsumption;

    return base;
  }


}
